/**
 * @file FreeShippingForm.tsx
 *
 * Inline controlled mini-form for adding or editing a free-shipping method within
 * the zone form (no react-hook-form). Fields: title and a `requires` condition (a
 * native `<select>`); when the chosen condition needs a minimum order amount, the
 * minimum-amount field and an "apply before discount" toggle appear. Validates the
 * title (required) and the minimum amount (finite, non-negative) on save, then
 * reports the clean {@link FreeShippingData} to its parent. Mirrors WSB-v2's
 * free-shipping sub-form.
 */

import { useId, useState } from 'react';
import { Button, Divider, GeneralField, Icon, Toggle } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { SHIPPING_REQUIREMENT_OPTIONS } from '../shipping-constants';
import { shippingRequirementNeedsMinimumAmount } from '../shipping-method-utils';
import { emptyFreeShippingData, isInvalidRequiredAmount } from './custom-shipping-form-utils';
import { requirementLabel } from './shipping-method-labels';
import type { FreeShippingData } from './custom-shipping-form-types';

interface FreeShippingFormProps {
  prev?: FreeShippingData;
  currencySymbol: string;
  /** Renders a divider + heading above the form (when added to a non-empty list). */
  showDivider?: boolean;
  onSubmit: (data: FreeShippingData) => void;
  onCancel: () => void;
}

interface FreeShippingErrors {
  title?: string;
  minAmount?: string;
}

/**
 * Renders the free-shipping method mini-form.
 *
 * @param props - The seed data (edit), currency symbol, divider flag, and the
 *   submit/cancel handlers.
 *
 * @returns The mini-form element.
 */
export function FreeShippingForm({
  prev,
  currencySymbol,
  showDivider = false,
  onSubmit,
  onCancel,
}: FreeShippingFormProps) {
  const intl = useIntl();
  const fieldId = useId();
  const [data, setData] = useState<FreeShippingData>(prev ?? emptyFreeShippingData());
  const [errors, setErrors] = useState<FreeShippingErrors>({});

  const needsMinimum = shippingRequirementNeedsMinimumAmount(data.requires);

  const patch = (update: Partial<FreeShippingData>): void => {
    setData((prevData) => ({ ...prevData, ...update }));
  };

  const handleSave = (): void => {
    const next: FreeShippingErrors = {};
    if (data.title.trim() === '') {
      next.title = intl.formatMessage(adminMessages.shippingMethodTitleRequired);
    }
    if (needsMinimum && isInvalidRequiredAmount(data.minAmount)) {
      next.minAmount = intl.formatMessage(adminMessages.shippingAmountInvalid);
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }
    onSubmit({
      ...data,
      title: data.title.trim(),
      minAmount: needsMinimum ? data.minAmount.trim() : '0',
      ignoreDiscounts: needsMinimum ? data.ignoreDiscounts : false,
    });
  };

  return (
    <div className="gv-flex gv-flex-col gv-gap-md">
      {showDivider && (
        <div className="gv-flex gv-flex-col gv-gap-sm">
          <Divider />
          <div className="gv-text-sm gv-text-bold">
            <FormattedMessage
              {...(prev ? adminMessages.shippingMethodEditHeading : adminMessages.shippingMethodAddNewHeading)}
            />
          </div>
        </div>
      )}

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={`${fieldId}-title`}>
          <FormattedMessage {...adminMessages.shippingMethodTitleLabel} />
        </label>
        <GeneralField
          id={`${fieldId}-title`}
          value={data.title}
          error={errors.title}
          data-testid="free-shipping-form-title"
          aria-label={intl.formatMessage(adminMessages.shippingMethodTitleLabel)}
          onChange={(event) => patch({ title: event.target.value })}
        />
      </div>

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={`${fieldId}-requires`}>
          <FormattedMessage {...adminMessages.shippingRequiresLabel} />
        </label>
        <div className="gv-input gv-input-select gv-w-full">
          <select
            id={`${fieldId}-requires`}
            value={data.requires}
            data-testid="free-shipping-form-requires"
            aria-label={intl.formatMessage(adminMessages.shippingRequiresLabel)}
            onChange={(event) => patch({ requires: event.target.value })}
          >
            {SHIPPING_REQUIREMENT_OPTIONS.map((option) => (
              <option key={option.value || 'none'} value={option.value}>
                {intl.formatMessage(requirementLabel(option.value))}
              </option>
            ))}
          </select>
          <Icon name="expand_more" size="small" />
        </div>
      </div>

      {needsMinimum && (
        <>
          <div className="gv-form-option gv-w-full">
            <label className="gv-label" htmlFor={`${fieldId}-min`}>
              <FormattedMessage {...adminMessages.shippingMinAmountLabel} />
            </label>
            <GeneralField
              id={`${fieldId}-min`}
              value={data.minAmount}
              inputMode="decimal"
              addon={currencySymbol}
              error={errors.minAmount}
              data-testid="free-shipping-form-min-amount"
              aria-label={intl.formatMessage(adminMessages.shippingMinAmountLabel)}
              onChange={(event) => patch({ minAmount: event.target.value })}
            />
            <p className="gv-caption-lg">
              <FormattedMessage {...adminMessages.shippingMinAmountHelp} />
            </p>
          </div>

          <label className="gv-flex gv-items-center gv-gap-sm">
            <Toggle
              condensed
              checked={data.ignoreDiscounts}
              onChange={(checked) => patch({ ignoreDiscounts: checked })}
            />
            <span className="gv-flex gv-flex-col gv-gap-xs">
              <span className="gv-text-sm">
                <FormattedMessage {...adminMessages.shippingIgnoreDiscountsLabel} />
              </span>
              <span className="gv-caption-lg gv-text-on-alternative">
                <FormattedMessage {...adminMessages.shippingIgnoreDiscountsDesc} />
              </span>
            </span>
          </label>
        </>
      )}

      <div className="gv-flex gv-gap-sm gv-flex-col-reverse gv-tab-flex-row">
        <Button
          variant="cancel"
          condensed
          data-testid="free-shipping-form-cancel"
          onClick={onCancel}
        >
          <FormattedMessage {...adminMessages.shippingMethodCancel} />
        </Button>
        <Button
          variant="primary"
          condensed
          data-testid="free-shipping-form-done"
          onClick={handleSave}
        >
          <FormattedMessage {...adminMessages.shippingMethodSave} />
        </Button>
      </div>
    </div>
  );
}
